import { Observable } from 'rxjs';
import { authorizedFetch } from '../core/authorizedFetch';
import i18n from '../i18n';
import {env} from "../config/environment";
import {toHttpError} from "../errors";

const BASE_URL = env.backendUrl;

export interface StreamMessageParams {
    conversationId: string;
    question: string;
    files: File[] | null;
    searchInternet?: boolean;
    retry?: boolean;
}

// TODO : fix conversationId type to number
export class StreamingService {
    createMessageStream(params: StreamMessageParams): Observable<string> {
        const { conversationId, question, files, searchInternet, retry } = params;

        return new Observable<string>(subscriber => {
            if (
                !conversationId ||
                String(conversationId) === 'undefined' ||
                String(conversationId) === 'null'
            ) {
                subscriber.error(new Error('Invalid conversationId: ' + conversationId));
                return;
            }

            const controller = new AbortController();
            let readerReleased = false;

            // type of reader encoded text
            const releaseReader = (reader: ReadableStreamDefaultReader<Uint8Array> | null) => {
                if (readerReleased || !reader) return;
                readerReleased = true;
                try {
                    reader.releaseLock();
                } catch (e) {
                    console.error(e);
                }
            };

            let reader: ReadableStreamDefaultReader<Uint8Array> | null = null;

            const startStream = async () => {
                try {
                    // safe way to send sensitive and long data (browsers and servers set limits on URL length)
                    const currentLanguage = i18n.language || 'pl';
                    const formData = new FormData();

                    formData.append('question', question);
                    formData.append('conversationId', conversationId);
                    formData.append('lang', currentLanguage);
                    formData.append('searchInternet', searchInternet ? 'true' : 'false');
                    formData.append('retry', retry ? 'true' : 'false');

                    if (files && files.length > 0) {
                        files.forEach(file => formData.append('files', file));
                    }
                    const url = `${BASE_URL}/chat`;

                    const response = await authorizedFetch(url, {
                        method: 'POST',
                        headers: {
                            Accept: 'text/event-stream',
                        },
                        body: formData,
                        signal: controller.signal,
                    });

                    if (!response.ok) {
                        throw await toHttpError(response);
                    }

                    if (!response.body) {
                        throw new Error('Response body is empty');
                    }

                    reader = response.body.getReader();

                    // decoding type
                    const decoder = new TextDecoder('utf-8');
                    let buffer = '';

                    try {
                        let reading = true;

                        while (reading) {
                            const { done, value } = await reader.read();

                            if (done) {
                                reading = false;
                                break;
                            }

                            // Todo: Refactor or delete! This causes format failure as it mimics Markdown instead of combining
                            //  markdown chunks to one string.
                            buffer += decoder.decode(value, { stream: true });

                            let idx: number;
                            while ((idx = buffer.indexOf('\n\n')) !== -1) {
                                const rawEvent = buffer.slice(0, idx);
                                buffer = buffer.slice(idx + 2);

                                const lines = rawEvent.split(/\r?\n/);
                                let dataPayload = '';
                                for (const line of lines) {
                                    const m = line.match(/^data:(.*)$/);
                                    if (m) dataPayload += m[1];
                                }

                                if (dataPayload === '') {
                                    subscriber.next('\n');
                                } else if (dataPayload === '_END_') {
                                    releaseReader(reader);
                                    subscriber.complete();
                                    return;
                                } else {
                                    subscriber.next(dataPayload);
                                }
                            }
                        }

                        subscriber.complete();
                    } catch (error) {
                        if (!controller.signal.aborted) {
                            subscriber.error(error);
                        }
                    } finally {
                        releaseReader(reader);
                    }
                } catch (error) {
                    if (!controller.signal.aborted) {
                        subscriber.error(error);
                    }
                }
            };

            startStream();

            return () => {
                controller.abort();
                reader?.cancel().catch(() => undefined);
            };
        });
    }
}

export const streamingService = new StreamingService();
